import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryService } from '../services/queryService';
import { getUserRoles, hasElevatedAdminAccess } from '../services/authService';
import { getAppRoot } from '../utils/queryUtils';

export interface ApplicationOption {
    applicationId: string;
    name?: string;
}

export const useApplicationAccess = () => {
    const { data: applications = [] } = useQuery({
        queryKey: ['applications'],
        queryFn: () => queryService.getApplications(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const accessibleApplications = React.useMemo((): ApplicationOption[] => {
        const userRoles = getUserRoles();

        const sorted = (list: any[]): ApplicationOption[] =>
            [...list].sort((a, b) =>
                (a.name || a.applicationId).localeCompare(b.name || b.applicationId)
            );

        if (hasElevatedAdminAccess(userRoles)) {
            return sorted(applications);
        }

        return sorted(
            applications.filter((app: any) => {
                const appId = (app.applicationId || '').toLowerCase();
                const appName = (app.name || '').toLowerCase();
                const appRoot = getAppRoot(appId);
                return userRoles.some(role => {
                    const r = (role || '').toLowerCase();
                    const roleRoot = getAppRoot(r);
                    return roleRoot === appRoot || r === appName;
                });
            })
        );
    }, [applications]);

    const defaultApplicationId: string | null =
        accessibleApplications.length > 0 ? accessibleApplications[0].applicationId : null;

    const hasAccess = (applicationId?: string): boolean => {
        // If no application is assigned, it's considered accessible (global/legacy)
        if (!applicationId) return true;

        const userRoles = getUserRoles();

        if (hasElevatedAdminAccess(userRoles)) {
            return true;
        }

        // Check if user has the role assigned to this application ID
        const appId = applicationId.toLowerCase();
        const appRoot = getAppRoot(appId);
        return userRoles.some(role => {
            const r = (role || '').toLowerCase();
            const roleRoot = getAppRoot(r);
            return roleRoot === appRoot;
        });
    };

    const canCreate = (): boolean => {
        // Creating new queries has nothing to do with application ID access
        // Any user who can access the queries module can draft a query.
        return true;
    };

    return { hasAccess, canCreate, accessibleApplications, defaultApplicationId };
};
